import * as React from 'react';
import {RouteComponentProps} from 'react-router';
import {ChecklistSummaries, ChecklistSummary, EmailSendResponse, ErrorResponse, Rental} from '../../api/dtos/dtos';
import * as paths from '../../paths';
import {IdPageUrlParams} from '../../paths';
import furoApi from '../../api/FuroApi';
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from '../../app/ApplicationContext';
import {setAppHeaderSubtitle} from "../../app/AppHeader";
import locales from "../../locales";
import {ErrorAlert} from "../../common/ErrorAlert";
import SendChecklistForm from "./SendChecklistForm";
import {showConfirm} from "../../utils/dialogs";
import ChecklistDetailsSummary from "./ChecklistDetailsSummary";
import SectionHeaderFormItem from "../../common/form-items/SectionHeaderFormItem";
import {ChecklistFormMode, findAllNotIgnored, findSendoutRecipients, hasChecklistCountMatch, isAccepted} from "../dtos";
import {idFromPathParams} from "../../utils/router-utils";
import RentalDetailsAlert from "./RentalDetailsAlert";
import {Button} from "antd";
import {showEmailSendResultModal} from "../../common/EmailSendResultModal";

interface State {
  rental?: Rental;
  summary?: ChecklistSummary;
  error?: ErrorResponse;
}

class SendRentalChecklistsPage extends React.Component<RouteComponentProps<IdPageUrlParams>, State> {

  public readonly loadingHandler?: LoadingOverlayHandler;

  constructor(props: RouteComponentProps<{}>) {
    super(props);
    this.state = {};
  }

  public componentDidMount() {
    this.loadRental();
    this.loadRentalChecklists();
  }

  public render() {
    const {rental, summary, error} = this.state;

    const accepted = isAccepted(summary);
    const checklistCountMatch = hasChecklistCountMatch(summary);
    const checklists = findAllNotIgnored(summary);
    const recipients = findSendoutRecipients(summary) ?? [];

    const initialContactIds: number[] = recipients.map((r) => r.contact.id);

    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>
        {rental &&
            <RentalDetailsAlert
                rental={rental}
                rentalTitle={locales.rental}
            />
        }
        {rental && summary &&
            <SendChecklistForm
                accepted={accepted}
                disabled={accepted || !checklistCountMatch}
                clientId={rental.client!.id!}
                initialContactIds={initialContactIds}
                onFormCancel={this.onFormCancel}
                onFormSubmit={this.onRentalFormSubmit}
            >
              {checklists &&
                  <div>
                    {checklists.map((checklist) => {
                      const onEdit = () => this.onChecklistEdit(checklist.id!);

                      return (
                        <div key={checklist.id!}>
                          <SectionHeaderFormItem
                            title={
                              <>
                                {checklist.rentalItem.item.name}
                                {!accepted && <Button icon="edit"
                                                      style={{marginLeft: 8}}
                                                      onClick={onEdit}
                                                      title={locales.edit}/>
                                }
                              </>
                            }
                          />
                          <ChecklistDetailsSummary
                            rentalChecklist={checklist}
                            key={checklist.id!}
                            mode={ChecklistFormMode.VIEW_RENTAL}
                          />
                        </div>
                      );
                    })}
                  </div>
              }
            </SendChecklistForm>
        }
        <ErrorAlert style={{marginTop: '16px'}} error={error}/>
      </>
    );
  }

  private onFormCancel = () => {
    this.props.history.goBack();
  };

  private onRentalFormSubmit = (contactIds: number[]) => {
    showConfirm(locales.checklist_send_confirm, () => this.sendRentalChecklists(contactIds));
  };

  private sendRentalChecklists = (recipientContactIds: number[]) => {
    const rentalID = idFromPathParams(this.props);
    const promise = furoApi.checklist.sendRentalChecklists(rentalID, {recipientContactIds})
      .then(this.onSendResponse)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  }

  private onSendResponse = (response: EmailSendResponse) => {
    showEmailSendResultModal({
      title: locales.email_send_result_checklist,
      response,
      onSuccess: this.pop
    });
  };

  private pop = () => {
    this.props.history.goBack();
  }

  private loadRental = () => {
    const rentalID = idFromPathParams(this.props);
    const promise = furoApi.rentals.get(rentalID)
      .then(this.onRentalLoaded)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onRentalLoaded = (response: Rental) => {
    this.setState({rental: response});
  };

  private loadRentalChecklists = () => {
    const rentalID = idFromPathParams(this.props);
    const promise = furoApi.checklist.getRentalChecklists(rentalID)
      .then(this.loadChecklistsSuccess)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private loadChecklistsSuccess = (response: ChecklistSummaries) => {
    this.setState({summary: response.rental});
    setAppHeaderSubtitle(this.props, locales.checklist_send_out);
  };

  private onApiError = (error: ErrorResponse) => {
    this.setState({error});
  }

  private onChecklistEdit = (id: number) => {
    this.props.history.push(paths.viewChecklistPath(id));
  }
}

export default SendRentalChecklistsPage;




// WEBPACK FOOTER //
// ./src/checklist/send/SendRentalChecklistsPage.tsx